<script lang="ts" setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import { useRoute } from 'vue-router/composables'
import { useBookExposure } from '@/composables/tracks/useBookExposure'
import { superstoryTrack } from '@/track'
import { bookEvent, bookParams } from '@/composables/tracks/bookParameters'
import { brand, features, resources } from '@/config/brands'

const props = withDefaults(defineProps<{
  id: string // 唯一id，用于曝光
  src: string // 真实图片地址
  alt?: string // 图片alt
  cornerMarkText?: string | null // 栏目配置的书封角标
  placeholder?: string // 占位图地址
  isReportImp?: boolean // 是否曝光埋点
  extra?: Record<string, any> // 埋点扩展数据
  heightRate?: number
  ignorePicturesAppear?: boolean // 某些图片过大, 以object-cover显示时 intersectionRatio 永远不会大于0.5 导致无法曝光埋点, 配置这个直接忽略大于 0.5 的判断
  lazy?: boolean // 是否懒加载, 默认 true; swiper loop 等会克隆 DOM 的场景需置为 false, 否则克隆 slide 拿不到真实图片
}>(), {
  alt: '',
  placeholder: resources.bookCoverPlaceholder,
  isReportImp: true,
  heightRate: 133,
  ignorePicturesAppear: false,
  lazy: true,
})

const route = useRoute()
const { bookExposure } = useBookExposure()
// 非懒加载时直接渲染真实图片, 保证 swiper loop 克隆 slide 能拿到带 src 的图片节点
const imgSrc = ref(props.lazy ? '' : props.src)
const hasExposed = ref(false)
const isIntersecting = ref(false)
const imgLoaded = ref(false)
const ob = ref<any>(null)
const coverRef = ref<HTMLElement | null>(null)

function onImgError() {
  imgSrc.value = props.placeholder
}

function onImgLoad() {
  imgLoaded.value = true
}

async function loadImage(src: string) {
  if (!src) {
    imgSrc.value = ''
    imgLoaded.value = false
    return
  }

  if (imgSrc.value === src) {
    imgLoaded.value = true
    return
  }

  imgLoaded.value = false
  imgSrc.value = ''
  await nextTick()
  imgSrc.value = src
}

// 书籍身份变化时（如列表组件实例被复用、切换 tab）重置状态:
// 重新加载图片, 并重置曝光标记, 保证新书能再次触发 bookExposure
watch(() => [props.extra?.id, props.src], () => {
  hasExposed.value = false
  // 懒加载且当前不可见时先清空, 等进入视口再加载; 否则直接加载新图
  if (props.lazy && !isIntersecting.value) {
    imgSrc.value = ''
    imgLoaded.value = false
  }
  else {
    loadImage(props.src)
  }
})

// 监听曝光与懒加载
onMounted(() => {
  if (!coverRef.value)
    return

  ob.value = useIntersectionObserver(
    coverRef,
    ([{ isIntersecting: intersecting, intersectionRatio }]) => {
      isIntersecting.value = intersecting
      // 懒加载 - 可见度达到 10% 即开始加载
      if (intersecting && intersectionRatio >= 0.1 && !imgLoaded.value)
        loadImage(props.src)

      // 曝光埋点 - 可见度达到 50% 才触发曝光
      if (intersecting && props.isReportImp && !hasExposed.value && (props.ignorePicturesAppear || intersectionRatio >= 0.5)) {
        if (brand === 'superstory' && props.extra?.id) {
          const values = { ...bookParams(props.extra, props.extra.pos), ...props.extra.tracking }
          if (['chapter', 'chapter-scroll', 'recommend'].includes(String(route.name)) || route.query.type === 'finish')
            superstoryTrack('finished_recommendation_exp', { ...values, book_id: route.params.contentId || route.params.columnId, recommend_book_id: props.extra.id })
          else
            superstoryTrack(bookEvent(String(route.name), 'exp'), values)
        }
        else if (brand !== 'superstory') {
          bookExposure({
            module_name: props.extra?.module_name || '',
            module_id: props.extra?.module_id || '',
            module_type: props.extra?.module_type || '',
            book_id: props.extra?.id || '',
            book_name: props.extra?.contentName || '',
            author: props.extra?.author || '',
          })
        }
        hasExposed.value = true
      }
    },
    {
      root: null,
      rootMargin: '0px',
      threshold: [0.1, 0.5], // 监听 10% 和 50% 两个阈值
    },
  )
})

onBeforeUnmount(() => {
  if (ob.value)
    ob.value.stop()
})
</script>

<template>
  <div class="overflow-hidden">
    <div
      :id="id"
      ref="coverRef"
      class="overflow-hidden w-full relative h-0"
      :style="{ paddingBottom: `${heightRate}%` }"
    >
      <img
        class="w-full left-0 absolute h-full object-cover top-0"
        :src="placeholder"
        :alt="alt"
      >

      <img
        v-if="imgSrc"
        class="w-full left-0 absolute h-full object-cover top-0 transition-opacity duration-300"
        :class="{ 'opacity-100': imgLoaded, 'opacity-0': !imgLoaded }"
        :src="imgSrc"
        :alt="alt"
        @load="onImgLoad"
        @error="onImgError"
      >
      <span v-if="features?.enableBookCoverCornerMark && cornerMarkText?.trim()" class="book-cover-corner">
        {{ cornerMarkText }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.book-cover-corner {
  position: absolute;
  top: 0;
  right: 0;
  max-width: 100%;
  box-sizing: border-box;
  padding: 2px 4px;
  border-radius: 0 0 0 4px;
  background: #00a1ff;
  color: #fff;
  font-size: 10px;
  line-height: 14px;
  white-space: normal;
  overflow-wrap: anywhere;
  pointer-events: none;
}
</style>
